import React from 'react';
import { MessageCircle } from 'lucide-react';
import { useAuth } from '../../../lib/auth';

const WHATSAPP_NUMBER = import.meta.env.VITE_SUPPORT_WHATSAPP_NUMBER ?? '+393932787927';

export interface WhatsAppSupportButtonProps {
  variant?: 'inline' | 'card';
  context?: string;
}

export function WhatsAppSupportButton({
  variant = 'inline',
  context,
}: WhatsAppSupportButtonProps) {
  const { user, userProfile } = useAuth();

  // userProfile.full_name is available; fall back to email-derived first name
  const firstName = React.useMemo(() => {
    const fullName = (userProfile as { full_name?: string } | null)?.full_name;
    if (fullName) return fullName.split(' ')[0] ?? fullName;
    if (!user?.email) return '';
    const local = user.email.split('@')[0] ?? '';
    return local.charAt(0).toUpperCase() + local.slice(1);
  }, [user?.email, userProfile]);

  const baseMessage = context ?? 'Sto usando Flusia e mi servirebbe aiuto';
  const fullMessage = `Ciao! ${baseMessage}. Mi chiamo ${firstName}.`;

  const href = `https://wa.me/${WHATSAPP_NUMBER.replace(/\D/g, '')}?text=${encodeURIComponent(fullMessage)}`;

  if (variant === 'card') {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-5">
        <div className="flex items-start gap-3">
          <div
            className="shrink-0 w-10 h-10 rounded-xl inline-flex items-center justify-center"
            style={{ backgroundColor: '#25D366' }}
          >
            <MessageCircle className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-slate-900 dark:text-slate-100">
              Serve aiuto?
            </div>
            <div className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-0.5">
              Scrivici su WhatsApp, ti rispondiamo in poche ore.
            </div>
          </div>
        </div>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-white text-[14px] font-semibold transition-opacity hover:opacity-90"
          style={{ backgroundColor: '#25D366' }}
        >
          <MessageCircle className="w-4 h-4" strokeWidth={2} />
          Scrivi su WhatsApp
        </a>
      </div>
    );
  }

  // inline variant
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-[12.5px] font-medium transition-opacity hover:opacity-80"
      style={{ color: '#25D366' }}
    >
      <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />
      Hai bisogno di aiuto?
    </a>
  );
}
